import { ScrollArea } from '@neo/primitives/scroll-area'
import { Skeleton } from '@neo/primitives/skeleton'
import _, { startCase } from 'lodash'
import { Eye, FileText } from 'lucide-react'
import { AppSourceSkeleton } from '../app-source-skeleton'
import TreeView, { type TreeViewItem } from '../document-tree-view'

interface AppDocumentTreeProps {
  selectedAppProvider: string
  isConnected: boolean
  urlSelectionType: string
  getAppLogo: (provider: string) => string | undefined
  isAppLoading: boolean
  totalPageCount: number
  collectionCount: number
  loosePageCount: number
  selectedCount: number
  treeData: TreeViewItem[]
  defaultCheckedIds: string[]
  onCheckChange: (item: TreeViewItem, isChecked: boolean) => void
  onNewSelectionChange: (items: TreeViewItem[]) => void
  onTreeAction: (action: string, items: TreeViewItem[]) => void
  onExpand?: (item: TreeViewItem) => void
}

export const AppDocumentTree = ({
  selectedAppProvider,
  isConnected,
  urlSelectionType,
  getAppLogo,
  isAppLoading,
  totalPageCount,
  collectionCount,
  loosePageCount,
  selectedCount,
  treeData,
  defaultCheckedIds,
  onCheckChange,
  onNewSelectionChange,
  onTreeAction,
  onExpand,
}: AppDocumentTreeProps) => {
  if (!selectedAppProvider || !isConnected || urlSelectionType !== 'selected') {
    return null
  }

  // Memoize iconMap for TreeView
  const treeIconMap = {
    article: <FileText className="h-4 w-4" />,
  }

  // Memoize menuItems for TreeView
  const treeMenuItems = [
    {
      id: 'preview',
      label: 'Preview',
      icon: <Eye className="h-4 w-4" aria-label="Preview document" data-testid="icon-preview" />,
      action: (items: TreeViewItem[]) => onTreeAction('preview', items),
    },
  ]

  return (
    <div className="w-full max-w-[628px] mt-4 p-4 rounded-md border border-st2 bg-background inline-flex flex-col justify-start items-start gap-4">
      <div className="self-stretch inline-flex justify-start items-center gap-2">
        {getAppLogo(selectedAppProvider) ? (
          <img
            src={getAppLogo(selectedAppProvider)}
            alt={`${startCase(selectedAppProvider)} logo`}
            className="w-5 h-5 rounded"
          />
        ) : (
          <div className="w-5 h-5 relative bg-bg3 rounded flex items-center justify-center">
            <div className="text-foreground text-xs-600">
              {selectedAppProvider.charAt(0).toUpperCase()}
            </div>
          </div>
        )}
        <div className="text-foreground text-xs-400 flex items-center gap-1.5">
          {isAppLoading ? (
            <Skeleton className="w-[120px] h-3" />
          ) : (
            <>
              {/* Top-level count: spaces/collections for Confluence/Zendesk, pages for a flat source.
                  (The old "{n} pages available" counted only lazily-LOADED pages — misleading while the
                  tree listed every space.) A source that mixes spaces AND loose top-level pages shows
                  both so the loose pages aren't hidden behind "N spaces". */}
              <span>
                {collectionCount > 0
                  ? `${collectionCount} ${collectionCount === 1 ? 'space' : 'spaces'}${
                      loosePageCount > 0
                        ? ` · ${loosePageCount} ${loosePageCount === 1 ? 'page' : 'pages'}`
                        : ''
                    }`
                  : `${totalPageCount} ${totalPageCount === 1 ? 'page' : 'pages'}`}
              </span>
              {selectedCount > 0 && (
                <span className="text-secondary">· {selectedCount} selected</span>
              )}
            </>
          )}
        </div>
      </div>
      <div className="self-stretch flex flex-col justify-start items-start gap-3">
        {(() => {
          if (isAppLoading) {
            return <AppSourceSkeleton />
          }
          if (_.isEmpty(treeData)) {
            return (
              <div className="p-4 text-secondary text-s-400 w-full">
                No documents found in {startCase(selectedAppProvider)}
              </div>
            )
          }
          // Cap the Radix VIEWPORT (viewportClassName), not the Root: the viewport is `h-full`, so a
          // `max-h` on the Root alone can't bound it and the list grew to its full intrinsic height
          // (11k+ pages), overrunning the "Sync data" button. max-h on the overflow:scroll viewport
          // itself bounds it for any content size. 180px ≈ 5-6 rows then scroll.
          return (
            <ScrollArea className="w-full pr-2 -mr-2" viewportClassName="max-h-[180px]">
              <TreeView
                data={treeData}
                showCheckboxes
                onAction={onTreeAction}
                selectionText="selected documents"
                className="w-full"
                showExpandAll={false}
                showSearchBox={false}
                showItemDetailsOnHover={false}
                iconMap={treeIconMap}
                menuItems={treeMenuItems}
                showSelectionHeader={false}
                showSelectionBadges={false}
                onCheckChange={onCheckChange}
                defaultCheckedIds={defaultCheckedIds}
                onNewSelectionChange={onNewSelectionChange}
                onExpand={onExpand}
              />
            </ScrollArea>
          )
        })()}
      </div>
    </div>
  )
}
